import has from '@dojo/framework/core/has';
import { dimensions } from '@dojo/framework/core/middleware/dimensions';
import { focus } from '@dojo/framework/core/middleware/focus';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { Button } from '@dojo/widgets/button';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { Tooltip } from '@dojo/widgets/tooltip';
import { Icon } from '@holmescorp/base-widgets';
import { convertToBoolean, Keys } from '@holmescorp/common';
import { createNlsMiddleware, expandedTopicChange, lccstate, userstate } from '@holmescorp/lcc-state';
import { ParentTree, TopicDetails } from '@holmescorp/lcc-state/interfaces';
import { capitalize } from 'lodash-es';
import css from '../../css/tgf.m.css';
import { CardProperties, ConditionalFilters } from '../../interfaces';
import { Card } from '../../widgets/index';
import { ExpandedTopicCard } from './expendedTopic';

export interface TGFCardProperties extends CardProperties<boolean> {
	key: string;
	domain: string;
	completed: boolean;
	percent: number;
	title: string;
	topic: TopicDetails;
	xqa?: string;
	flagged?: boolean;
	pretest?: number;
	isreadingstarted?: boolean;
	isreadingcompleted?: boolean;
	isquizstarted?: boolean;
	isquizcompleted?: boolean;
	action?: (key: string) => void;
	index?: number;
}

export interface TGFCardICache {
	expanded: boolean;
	promote_ttopen: boolean;
	demote_ttopen: boolean;
	should_focus: boolean;
}

const icache = createICacheMiddleware<TGFCardICache>();
const ditabookNls = createNlsMiddleware('ditabook');

const factory = create({
	icache,
	theme,
	ditabookNls,
	dimensions,
	userstate,
	lccstate,
	focus
}).properties<TGFCardProperties>();

export const TGFCard = factory(
	({ id, properties, middleware: { icache, theme, dimensions, ditabookNls, userstate, lccstate, focus } }) => {
		const themeCss = theme.classes(css);
		const {
			xqa,
			key,
			domain,
			percent,
			title,
			topic,
			pretest = 0,
			isreadingstarted = false,
			isreadingcompleted = false,
			isquizstarted = false,
			isquizcompleted = false,
			ispromotable = true,
			ispromoted = false,
			action,
			index = 0
		} = properties();
		const {
			messages: {
				topic_domain_mappings = '{}',
				pretest_indicator_threshold = '66.7',
				quiz_score_threshold = '80',
				card_button_label_start = '[ditabook] card_button_label_start',
				card_button_label_middle = '[ditabook] card_button_label_middle',
				card_button_label_finished = '[ditabook] card_button_label_finished',
				card_subtitle_reading_start = '[ditabook] card_subtitle_reading_start',
				card_subtitle_reading_continue = '[ditabook] card_subtitle_reading_continue',
				card_subtitle_reading_finish = '[ditabook] card_subtitle_reading_finish',
				card_subtitle_quiz_start = '[ditabook] card_subtitle_quiz_start',
				card_subtitle_quiz_finish = '[ditabook] card_subtitle_quiz_finish',
				card_promote_tooltip = '',
				card_demote_tooltip = '',
				card_flagged_tooltip = 'This area is flagged for review due to a low score',
				conditional_topic_filters = '{}',
				conditional_filter_state_key = 'landing',
				recommended_to_flag = '3',
				card_show_topic_domain_label = 'true',
				is_demo = 'false'
			}
		} = ditabookNls();

		const isDemo = convertToBoolean(is_demo);

		const { get, path } = userstate;

		let domainMappings: { [key: string]: string } = {};
		let parsedFilters: ConditionalFilters = {};
		try {
			domainMappings = JSON.parse(topic_domain_mappings);
			parsedFilters = JSON.parse(conditional_topic_filters);
		} catch (e) {
			domainMappings = {};
			parsedFilters = {};
		}
		// check if the TGF has a custom domain value
		const domainLabel = domainMappings[key] || domain;

		const showTopicDomain = convertToBoolean(card_show_topic_domain_label);

		const isFlaggable = (key: string) => {
			const toolstate = get(path('toolstate', conditional_filter_state_key));

			if (key in parsedFilters === false || !toolstate) {
				return true;
			}

			const filter = parsedFilters[key];
			return filter && toolstate[filter.filterby] && toolstate[filter.filterby] === filter.condition;
		};

		const assessmentResults: ParentTree = get(path('assessment')) || {};
		let flagged = false;
		if (has('use-ml-study-plan')) {
			const assessOrder = assessmentResults[key] && assessmentResults[key].data.order;
			flagged =
				isFlaggable(key) &&
				assessmentResults[key] &&
				assessOrder != null &&
				assessOrder < parseInt(recommended_to_flag);
		} else {
			const assessScore = assessmentResults[key] && assessmentResults[key].data.percent;
			flagged =
				isFlaggable(key) && assessmentResults[key] && assessScore < parseFloat(pretest_indicator_threshold);
		}

		const activitiesCompleted = isreadingcompleted && isquizcompleted;

		const which_label = () => {
			const pretestThreshold = parseFloat(quiz_score_threshold);

			// review label
			if (
				isquizcompleted ||
				(pretest >= pretestThreshold && !isreadingstarted) ||
				(!isquizcompleted && !isquizstarted && isreadingcompleted)
			) {
				return card_button_label_finished;
			}

			if (isquizstarted) {
				return pretest >= pretestThreshold && !isreadingstarted
					? card_button_label_finished
					: card_button_label_middle;
			}

			// begin label
			if (!isreadingstarted && pretest < pretestThreshold) {
				return card_button_label_start;
			}

			return card_button_label_middle;
		};

		const which_subtitle = () => {
			if (activitiesCompleted) {
				return '';
			}

			if (isquizstarted && (!isreadingstarted || isreadingcompleted)) {
				return card_subtitle_quiz_finish;
			}

			if (!isreadingstarted) {
				return card_subtitle_reading_start;
			}

			if (isreadingcompleted && percent === 0) {
				return card_subtitle_quiz_start;
			}

			if (isreadingstarted) {
				return isquizcompleted ? card_subtitle_reading_finish : card_subtitle_reading_continue;
			}

			return '';
		};

		const promote_actions = v(
			'div',
			{
				classes: [
					themeCss.promote,
					!ispromotable || (isDemo && index !== 0) ? themeCss.disabledPromote : null,
					'cardActions'
				],
				xqa: 'tgf-card-actions',
				role: 'button',
				disabled: isDemo && index !== 0,
				tabIndex: 0,
				...formatAriaProperties({
					label: ispromoted ? `Remove ${title} from my study plan` : `Add ${title} to my study plan`
				}),
				onclick: () => {
					if (ispromotable && !(isDemo && index !== 0) && action) {
						action(key);
					}
					icache.set('promote_ttopen', false);
					icache.set('demote_ttopen', false);
				},
				onkeydown: (event) => {
					switch (event.key) {
						case Keys.Enter:
						case Keys.Space:
						case Keys.Spacebar:
							event.preventDefault();
							if (ispromotable && !(isDemo && index !== 0) && action) {
								action(key);
							}
							icache.set('promote_ttopen', false);
							icache.set('demote_ttopen', false);
							break;
						case Keys.Escape:
							icache.set('promote_ttopen', false);
							icache.set('demote_ttopen', false);
							break;
					}
				},
				onmouseover: () => ispromotable && icache.set(`${ispromoted ? 'demote' : 'promote'}_ttopen`, true),
				onmouseout: () => ispromotable && icache.set(`${ispromoted ? 'demote' : 'promote'}_ttopen`, false),
				onfocus: () => ispromotable && icache.set(`${ispromoted ? 'demote' : 'promote'}_ttopen`, true),
				onblur: () => ispromotable && icache.set(`${ispromoted ? 'demote' : 'promote'}_ttopen`, false)
			},
			[
				ispromoted
					? w(
							Tooltip,
							{
								open: icache.getOrSet('demote_ttopen', false),
								orientation: 'top'
							},
							[
								{
									content: v('span', {}, [card_demote_tooltip]),
									trigger: v(
										'span',
										{
											xqa: 'tgf-card-action-demote'
										},
										[
											w(Icon, {
												fapre: 'far',
												faname: 'square-minus'
											})
										]
									)
								}
							]
					  )
					: w(
							Tooltip,
							{
								open: icache.getOrSet('promote_ttopen', false),
								orientation: 'top'
							},
							[
								{
									content: v('span', {}, [card_promote_tooltip]),
									trigger: v(
										'span',
										{
											xqa: 'tgf-card-action-promote'
										},
										[
											w(Icon, {
												fapre: 'far',
												faname: 'square-plus'
											})
										]
									)
								}
							]
					  )
			]
		);

		const { get: lget, path: lpath } = lccstate;
		const expandedTopic = lget(lpath('expanded_topic'));
		const isExpanded = expandedTopic === key;
		if (isExpanded) {
			expandedTopicChange({ topicid: '' });
		}
		const expanded = icache.getOrSet('expanded', isExpanded);

		const maybeFocus = () => {
			if (icache.get('should_focus')) {
				focus.focus();
				icache.set('should_focus', false);
			}
		};

		const imageExtension = has('use-svg-card-header-images') ? 'svg' : 'png';

		return v('div', { id, key, xqa, classes: [theme.variant(), themeCss.root] }, [
			expanded
				? w(ExpandedTopicCard, {
						expanded,
						topic,
						xqa: `${key}-expanded`,
						height: dimensions.get(key).size.height,
						onRequestClose: () => {
							icache.set('expanded', false);
							icache.set('should_focus', true);
						}
				  })
				: null,
			w(
				Card,
				{
					xqa: `card-${domain}-${key}`,
					mediaSrc: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/card_header_${key}.${imageExtension}`,
					mediaTitle: capitalize(domain),
					disabled: activitiesCompleted
				},
				[
					{
						content: v('div', { classes: [themeCss.content] }, [
							ispromoted === undefined ? null : promote_actions,
							showTopicDomain
								? v('div', { classes: [themeCss.domain], xqa: 'tgf-card-domain-label' }, [domainLabel])
								: null,
							v('div', { classes: [themeCss.title], xqa: 'tgf-card-title' }, [title]),
							v('div', { classes: [themeCss.subtitle], xqa: 'tgf-card-subtitle' }, [which_subtitle()]),
							v('div', { classes: [themeCss.actions], xqa: 'tgf-card-button' }, [
								w(
									Button,
									{
										disabled: isDemo && index !== 0,
										focus: () => icache.get('should_focus') || false,
										onFocus: maybeFocus,
										onClick: () => {
											icache.set('expanded', true);
										},
										aria: {
											label: `${which_subtitle().replace(
												/[^a-zA-Z0-9\s]/g,
												''
											)} for the ${title} area`
										},
										iconPosition: 'after'
									},
									[
										{
											icon:
												isDemo && index !== 0
													? v('span', { styles: { paddingLeft: '5px' } }, [
															w(Icon, {
																...formatAriaProperties({ hidden: 'true' }),
																faname: 'lock'
															})
													  ])
													: undefined,
											label: which_label()
										}
									]
								)
							]),
							flagged
								? v(
										'div',
										{
											classes: [themeCss.indicator],
											xqa: 'tgf-card-indicator'
										},
										[
											v(
												'span',
												{
													classes: ['fa-stack', 'fa-1x'],
													role: 'img',
													title: card_flagged_tooltip,
													...formatAriaProperties({
														label: card_flagged_tooltip
													})
												},
												[
													v('i', {
														...formatAriaProperties({ hidden: 'true' }),
														classes: ['fa-regular', 'fa-circle', 'fa-stack-2x']
													}),
													v('i', {
														...formatAriaProperties({ hidden: 'true' }),
														classes: ['fa-solid', 'fa-exclamation', 'fa-stack-1x']
													})
												]
											)
										]
								  )
								: null
						])
					}
				]
			)
		]);
	}
);

export default TGFCard;
